'use client'
import { Box, Flex, Popover, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import LimitOrderIcon from '@/app/[locale]/price/stocks/[symbol]/_components/Icon/LimitOrder'
import MarketOrderIcon from '@/app/[locale]/price/stocks/[symbol]/_components/Icon/MarketOrder'
import RecurringBuyIcon from '@/app/[locale]/price/stocks/[symbol]/_components/Icon/RecurringBuy'
import ChevronDown from '@/assets/icons/chevron-down.svg'

import styles from './index.module.css'

export type OrderType = 'market' | 'limit' | 'recurring'

interface OrderOption {
  id: OrderType
  icon: React.ReactNode
  title: string
  description: string
}

interface OrderTypeSelectorProps {
  value: OrderType
  onChange: (value: OrderType) => void
}

export default function OrderTypeSelector({ value, onChange }: OrderTypeSelectorProps) {
  const [opened, setOpened] = useState(false)

  const t = useTranslations('tradeWidget')

  const orderOptions: OrderOption[] = [
    {
      id: 'market',
      icon: <MarketOrderIcon />,
      title: t('market_order'),
      description: t('market_order_description'),
    },
    {
      id: 'limit',
      icon: <LimitOrderIcon />,
      title: t('limit_order'),
      description: t('limit_order_description'),
    },
    {
      id: 'recurring',
      icon: <RecurringBuyIcon />,
      title: t('recurring_buy'),
      description: t('recurring_buy_description'),
    },
  ]

  const selectedOption = orderOptions.find(option => option.id === value) || orderOptions[0]

  const handleSelect = (optionId: OrderType) => {
    onChange(optionId)
    setOpened(false)
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={400}
      offset={8}
      styles={{
        dropdown: {
          background: 'var(--bg-surface-secondary)',
          border: '1px solid var(--border-surface-default)',
          borderRadius: 8,
          padding: 0,
        },
      }}
    >
      <Popover.Target>
        <Flex
          bg={'var(--bg-surface-secondary)'}
          className={styles.selector}
          onClick={() => setOpened(o => !o)}
        >
          <Text variant={'subtitle'}>{selectedOption.title}</Text>
          <Box ml={8}>
            <ChevronDown
              fill={'var(--content-secondary)'}
              width={20}
              height={20}
              style={{
                transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        </Flex>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap={0} className={styles.dropdown}>
          {orderOptions.map(option => (
            <Flex
              key={option.id}
              className={styles.orderOption}
              onClick={() => handleSelect(option.id)}
            >
              <Box>{option.icon}</Box>
              <Stack gap={4} flex={1} ml={8}>
                <Text variant={'body'} c={'#F7F9FA'} fw={500}>
                  {option.title}
                </Text>
                <Text variant={'subtitle'} c={'var(--content-secondary)'} lh="20px">
                  {option.description}
                </Text>
              </Stack>
              {value === option.id && (
                <Flex className={styles.checkmark} align="center" justify="center">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M13.3337 4L6.00033 11.3333L2.66699 8"
                      stroke="var(--content-primary)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Flex>
              )}
            </Flex>
          ))}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
